import { AxiosResponse } from 'axios';
import { runInAction } from 'mobx';

import { UnreachableFunction } from 'ui/base/types';

import { ResultKind } from 'services/base/types';
import { createRequestString, getResponseBytes, http, isHttpError } from 'services/base/http';

import { createPangolinDomain } from 'services/pangolin/loaders';

import {
  UpdateRestoreStatusMap,
  UpdateRestoreStatus as UpdateRestoreStatusEnum,
  UpdateUpgradeStatusMap,
  UpdateUpgradeStatus as UpdateUpgradeStatusEnum,
} from 'services/pangolin/protos/update_pb';

import {
  UpdateRestoreStatus,
  UpdateUpgradeStatus,
} from 'services/pangolin/types';

import { GlobalPangolinDomains } from 'services/pangolin/pangolin_domain';
import { UpdateRestoreService } from 'services/pangolin/update_restore_service';
import { UpdateSnapshotService } from 'services/pangolin/update_snapshot_service';
import { UpdateUpgradeService } from 'services/pangolin/update_upgrade_service';
import { UpdateReleaseService } from 'services/pangolin/update_release_service';

import {
  ClaimPangolinRequest,
  ClaimPangolinResponse,
  UnclaimPangolinRequest,
  UnclaimPangolinResponse,
  UpdatePangolinRequest,
  UpdatePangolinResponse,
} from './protos/pangolin_pb';

import {
  BandwidthUnit,
  ColorCode,
  IAccountsDomain,
  Pangolin,
  convertFromBandwidthUnits,
  createPangolinObject,
} from './types';

export enum ClaimPangolinKind {
  NONCE,
  MNEMONIC,
}

export type ClaimPangolinResult =
  | { kind: ResultKind.OK; pangolin: Pangolin }
  | { kind: ResultKind.SYSTEM_ERROR; status?: number; }
  | { kind: ResultKind.USER_ERROR; status?: number;
      registrationNonceError?: string;
      registrationNonceMnemonicError?: string; };

type ClaimArgs =
  | { kind: ClaimPangolinKind.NONCE; registrationNonce: string }
  | { kind: ClaimPangolinKind.MNEMONIC; registrationNonceMnemonic: string };

export type ClaimPangolinProps = {
  domain: IAccountsDomain;
} & ClaimArgs;

export const claim = async(args: ClaimPangolinProps): Promise<ClaimPangolinResult> => {
  const {
    domain,
    ...claimArgs
  } = args;

  try {
    const request = new ClaimPangolinRequest();
    if (claimArgs.kind === ClaimPangolinKind.NONCE) {
      request.setRegistrationNonce(claimArgs.registrationNonce);
    } else {
      request.setRegistrationNonceMnemonic(claimArgs.registrationNonceMnemonic);
    }
    const httpResp = await http.post('/pangolin/', createRequestString(request));
    return runInAction(() => {
      const pangolin = applyClaimPangolinResponse(domain, httpResp);
      return { kind: ResultKind.OK, pangolin };
    });
  } catch (error) {
    if (isHttpError(error)) {
      if (error.response?.status && 400 <= error.response.status && error.response.status < 500) {
        const httpResp = error.response;
        const response = ClaimPangolinResponse.deserializeBinary(getResponseBytes(httpResp));
        return {
          kind: ResultKind.USER_ERROR,
          status: error.response.status,
          registrationNonceError: response.getRegistrationNonceError(),
          registrationNonceMnemonicError: response.getRegistrationNonceMnemonicError(),
        };
      }
      if (error.response?.status && 500 <= error.response.status && error.response.status < 600) {
        return { kind: ResultKind.SYSTEM_ERROR, status: error.response.status };
      }
    }
    return { kind: ResultKind.SYSTEM_ERROR };
  }
};

function applyClaimPangolinResponse(domain: IAccountsDomain, httpResp: AxiosResponse): Pangolin {
  const response = ClaimPangolinResponse.deserializeBinary(getResponseBytes(httpResp));
  const pangolinDTO = response.getPangolin();
  if (pangolinDTO != null) {
    const pangolin = createPangolinObject(pangolinDTO);
    domain.pangolins.set(pangolin.id, pangolin);
    createPangolinDomain(pangolin, pangolin.id);
    return pangolin;
  } else {
    throw new Error('Invalid response');
  }
}

export type UpdatePangolinArgs = {
  domain: IAccountsDomain;
  id: string;
  nickname?: string;
  colorCode?: ColorCode;
  timezone?: string;
  bandwidthUploadString?: string;
  bandwidthUploadUnits?: BandwidthUnit;
  bandwidthDownloadString?: string;
  bandwidthDownloadUnits?: BandwidthUnit;
  enableBandwidthControls?: boolean;
  enableBufferbloatPrevention?: boolean;
  pendingUpdatePopupDisplay?: boolean;
  redirectModeBridgeEnabled?: boolean;
  redirectModeUnicastEnabled?: boolean;
  redirectModeUnicastTypeResponse?: boolean;
  redirectModeUnicastTypeRequest?: boolean;
  redirectModeUnicastNetworkInternet?: boolean;
  redirectModeUnicastNetworkInternal?: boolean;
  redirectModeBroadcastEnabled?: boolean;
  redirectModeBroadcastHostGateways?: boolean;
  redirectModeBroadcastHostHosts?: boolean;
  redirectModeBroadcastTypeResponse?: boolean;
  redirectModeBroadcastTypeRequest?: boolean;
  masqueradeEgress?: boolean;
  scannerEnableScanMdns?: boolean;
  scannerEnableScanNbios?: boolean;
  scannerEnableScanNmap?: boolean;
  scannerEnableScanRdns?: boolean;
  scannerEnableScanUpnp?: boolean;
};

export type UpdatePangolinResult =
  | { kind: ResultKind.OK; pangolin: Pangolin }
  | { kind: ResultKind.SYSTEM_ERROR; status?: number; }
  | { kind: ResultKind.USER_ERROR; status?: number;
      nicknameError?: string;
      colorCodeError?: string;
      bandwidthUploadStringError?: string;
      bandwidthDownloadStringError?: string; };

export const update = async(args: UpdatePangolinArgs): Promise<UpdatePangolinResult> => {
  const {
    domain,
    ...updateArgs
  } = args;

  try {
    // Construct the request
    const request = new UpdatePangolinRequest();
    request.setId(updateArgs.id);
    if (updateArgs.nickname != null) {
      request.setNickname(updateArgs.nickname);
    }
    if (updateArgs.colorCode != null) {
      request.setColorCode(updateArgs.colorCode);
    }
    if (updateArgs.timezone != null) {
      request.setTimezone(updateArgs.timezone);
    }
    if (updateArgs.bandwidthUploadString != null) {
      request.setBandwidthUploadString(updateArgs.bandwidthUploadString);
    }
    if (updateArgs.bandwidthUploadUnits != null) {
      request.setBandwidthUploadUnits(convertFromBandwidthUnits(updateArgs.bandwidthUploadUnits));
    }
    if (updateArgs.bandwidthDownloadString != null) {
      request.setBandwidthDownloadString(updateArgs.bandwidthDownloadString);
    }
    if (updateArgs.bandwidthDownloadUnits != null) {
      request.setBandwidthDownloadUnits(convertFromBandwidthUnits(updateArgs.bandwidthDownloadUnits));
    }
    if (updateArgs.enableBandwidthControls != null) {
      request.setEnableBandwidthControls(updateArgs.enableBandwidthControls);
    }
    if (updateArgs.enableBufferbloatPrevention != null) {
      request.setEnableBufferbloatPrevention(updateArgs.enableBufferbloatPrevention);
    }
    if (updateArgs.pendingUpdatePopupDisplay != null) {
      request.setMenderPendingUpdatePopupDisplay(updateArgs.pendingUpdatePopupDisplay);
    }
    if (updateArgs.redirectModeBridgeEnabled != null) {
      request.setRedirectModeBridgeEnabled(updateArgs.redirectModeBridgeEnabled);
    }
    if (updateArgs.redirectModeUnicastEnabled != null) {
      request.setRedirectModeUnicastEnabled(updateArgs.redirectModeUnicastEnabled);
    }
    if (updateArgs.redirectModeUnicastTypeResponse != null) {
      request.setRedirectModeUnicastTypeResponse(updateArgs.redirectModeUnicastTypeResponse);
    }
    if (updateArgs.redirectModeUnicastTypeRequest != null) {
      request.setRedirectModeUnicastTypeRequest(updateArgs.redirectModeUnicastTypeRequest);
    }
    if (updateArgs.redirectModeUnicastNetworkInternet != null) {
      request.setRedirectModeUnicastNetworkInternet(updateArgs.redirectModeUnicastNetworkInternet);
    }
    if (updateArgs.redirectModeUnicastNetworkInternal != null) {
      request.setRedirectModeUnicastNetworkInternal(updateArgs.redirectModeUnicastNetworkInternal);
    }
    if (updateArgs.redirectModeBroadcastEnabled != null) {
      request.setRedirectModeBroadcastEnabled(updateArgs.redirectModeBroadcastEnabled);
    }
    if (updateArgs.redirectModeBroadcastHostGateways != null) {
      request.setRedirectModeBroadcastHostGateways(updateArgs.redirectModeBroadcastHostGateways);
    }
    if (updateArgs.redirectModeBroadcastHostHosts != null) {
      request.setRedirectModeBroadcastHostHosts(updateArgs.redirectModeBroadcastHostHosts);
    }
    if (updateArgs.redirectModeBroadcastTypeResponse != null) {
      request.setRedirectModeBroadcastTypeResponse(updateArgs.redirectModeBroadcastTypeResponse);
    }
    if (updateArgs.redirectModeBroadcastTypeRequest != null) {
      request.setRedirectModeBroadcastTypeRequest(updateArgs.redirectModeBroadcastTypeRequest);
    }
    if (updateArgs.masqueradeEgress != null) {
      request.setMasqueradeEgress(updateArgs.masqueradeEgress);
    }
    if (updateArgs.scannerEnableScanMdns != null) {
      request.setScannerEnableScanMdns(updateArgs.scannerEnableScanMdns);
    }
    if (updateArgs.scannerEnableScanNbios != null) {
      request.setScannerEnableScanNbios(updateArgs.scannerEnableScanNbios);
    }
    if (updateArgs.scannerEnableScanNmap != null) {
      request.setScannerEnableScanNmap(updateArgs.scannerEnableScanNmap);
    }
    if (updateArgs.scannerEnableScanRdns != null) {
      request.setScannerEnableScanRdns(updateArgs.scannerEnableScanRdns);
    }
    if (updateArgs.scannerEnableScanUpnp != null) {
      request.setScannerEnableScanUpnp(updateArgs.scannerEnableScanUpnp);
    }

    // Run it and apply it
    const httpResp = await http.patch('/pangolin/', createRequestString(request));
    return runInAction(() => {
      const pangolin = applyUpdatePangolinResponse(domain, httpResp);
      return { kind: ResultKind.OK, pangolin };
    });
  } catch (error) {
    if (isHttpError(error)) {
      if (error.response?.status && 400 <= error.response.status && error.response.status < 500) {
        const httpResp = error.response;
        const response = UpdatePangolinResponse.deserializeBinary(getResponseBytes(httpResp));
        return {
          kind: ResultKind.USER_ERROR,
          status: error.response.status,
          nicknameError: response.getNicknameError(),
          colorCodeError: response.getColorCodeError(),
          bandwidthUploadStringError: response.getBandwidthUploadStringError(),
          bandwidthDownloadStringError: response.getBandwidthDownloadStringError(),
        };
      }
      if (error.response?.status && 500 <= error.response.status && error.response.status < 600) {
        return { kind: ResultKind.SYSTEM_ERROR, status: error.response.status };
      }
    }
    return { kind: ResultKind.SYSTEM_ERROR };
  }
};

function applyUpdatePangolinResponse(domain: IAccountsDomain, httpResp: AxiosResponse): Pangolin {
  const response = UpdatePangolinResponse.deserializeBinary(getResponseBytes(httpResp));
  const pangolinDTO = response.getPangolin();
  if (pangolinDTO != null) {
    const pangolin = createPangolinObject(pangolinDTO);
    domain.pangolins.set(pangolin.id, pangolin);
    return pangolin;
  } else {
    throw new Error('Invalid response');
  }
}


/*
Update just the online status of a given pangolin.
This will be called when an app update arrives.
*/
export type UpdatePangolinOnlineStatusArgs = {
  domain: IAccountsDomain;
  id: string;
  onlineStatus: boolean;
};

export const updateOnlineStatus = ({ domain, id, onlineStatus }: UpdatePangolinOnlineStatusArgs): boolean => {
  return runInAction(() => {
    const pangolin = domain.pangolins.get(id);
    if (pangolin != null) {
      pangolin.onlineStatus = onlineStatus;
      return true;
    }
    return false;
  });
};

export type UpdatePangolinUpgradeStatusArgs = {
  domain: IAccountsDomain;
  id: string;
  upgradeID: string;
  upgradeStatus: UpdateUpgradeStatusMap[keyof UpdateUpgradeStatusMap];
};

export const updateUpgradeStatus = async({ domain, id, upgradeID, upgradeStatus }: UpdatePangolinUpgradeStatusArgs): Promise<boolean> => {
  const pangolin = domain.pangolins.get(id);
  if (pangolin) {
    const domain = GlobalPangolinDomains.domains.get(id);
    const upgrade = (domain && (
      domain.updateUpgrades.get(upgradeID) ??
      await UpdateUpgradeService.fetchActive(pangolin, domain)
    ));
    const release = (domain && upgrade) && (
      domain.updateReleases.get(upgrade.target_release_id) ??
      await UpdateReleaseService.fetch(domain, upgrade.target_release_id)
    );
    if (upgrade && release) {
      switch (upgradeStatus) {
        case UpdateUpgradeStatusEnum.UPDATE_UPGRADE_STATUS_PENDING: {
          return runInAction(() => {
            upgrade.status = UpdateUpgradeStatus.PENDING;
            return true;
          });
        }
        case UpdateUpgradeStatusEnum.UPDATE_UPGRADE_STATUS_DOWNLOAD: {
          return runInAction(() => {
            upgrade.status = UpdateUpgradeStatus.DOWNLOAD;
            return true;
          });
        }
        case UpdateUpgradeStatusEnum.UPDATE_UPGRADE_STATUS_INSTALL: {
          return runInAction(() => {
            upgrade.status = UpdateUpgradeStatus.INSTALL;
            return true;
          });
        }
        case UpdateUpgradeStatusEnum.UPDATE_UPGRADE_STATUS_REBOOT: {
          return runInAction(() => {
            upgrade.status = UpdateUpgradeStatus.REBOOT;
            return true;
          });
        }
        case UpdateUpgradeStatusEnum.UPDATE_UPGRADE_STATUS_LAUNCH: {
          return runInAction(() => {
            upgrade.status = UpdateUpgradeStatus.LAUNCH;
            return true;
          });
        }
        case UpdateUpgradeStatusEnum.UPDATE_UPGRADE_STATUS_BACKUP: {
          return runInAction(() => {
            upgrade.status = UpdateUpgradeStatus.BACKUP;
            return true;
          });
        }
        case UpdateUpgradeStatusEnum.UPDATE_UPGRADE_STATUS_COMPLETED: {
          // refresh the list of snapshots
          await UpdateSnapshotService.fetchAll(domain);
          return runInAction(() => {
            upgrade.terminal_status = upgrade.status;
            upgrade.status = UpdateUpgradeStatus.COMPLETED;
            pangolin.updateActiveReleaseID = release.id;
            pangolin.updateActiveUpgradeID = undefined;
            return true;
          });
        }
        case UpdateUpgradeStatusEnum.UPDATE_UPGRADE_STATUS_CANCELLED: {
          // skip if upgrade service already set the status to cancelled
          if (upgrade.status === UpdateUpgradeStatus.CANCELLED) return true;
          return runInAction(() => {
            upgrade.terminal_status = upgrade.status;
            upgrade.status = UpdateUpgradeStatus.CANCELLED;
            pangolin.updateActiveUpgradeID = undefined;
            return true;
          });
        }
        case UpdateUpgradeStatusEnum.UPDATE_UPGRADE_STATUS_SKIPPED: {
          return runInAction(() => {
            upgrade.terminal_status = upgrade.status;
            upgrade.status = UpdateUpgradeStatus.SKIPPED;
            pangolin.updateActiveUpgradeID = undefined;
            return true;
          });
        }
        case UpdateUpgradeStatusEnum.UPDATE_UPGRADE_STATUS_FAILED: {
          return runInAction(() => {
            upgrade.terminal_status = upgrade.status;
            upgrade.status = UpdateUpgradeStatus.FAILED;
            pangolin.updateActiveUpgradeID = undefined;
            return true;
          });
        }
        default: {
          return UnreachableFunction(upgradeStatus);
        }
      }
    }
  }
  return false;
};

export type UpdatePangolinRestoreStatusArgs = {
  domain: IAccountsDomain;
  id: string;
  restoreID: string;
  restoreStatus: UpdateRestoreStatusMap[keyof UpdateRestoreStatusMap];
};

export const updateRestoreStatus = async({ domain, id, restoreID, restoreStatus }: UpdatePangolinRestoreStatusArgs): Promise<boolean> => {
  const pangolin = domain.pangolins.get(id);
  if (pangolin) {
    const domain = GlobalPangolinDomains.domains.get(id);
    const restore = (domain && (
      domain.updateRestores.get(restoreID) ??
      await UpdateRestoreService.fetchActive(pangolin, domain)
    ));
    const release = ((domain && restore) && (
      domain.updateReleases.get(restore.target_release_id) ??
      await UpdateReleaseService.fetch(domain, restore.target_release_id)
    ));
    if (restore && release) {
      switch (restoreStatus) {
        case UpdateRestoreStatusEnum.UPDATE_RESTORE_STATUS_PENDING: {
          return runInAction(() => {
            restore.status = UpdateRestoreStatus.PENDING;
            return true;
          });
        }
        case UpdateRestoreStatusEnum.UPDATE_RESTORE_STATUS_ROLLBACK: {
          return runInAction(() => {
            restore.status = UpdateRestoreStatus.ROLLBACK;
            return true;
          });
        }
        case UpdateRestoreStatusEnum.UPDATE_RESTORE_STATUS_DISPATCH: {
          return runInAction(() => {
            restore.status = UpdateRestoreStatus.DISPATCH;
            return true;
          });
        }
        case UpdateRestoreStatusEnum.UPDATE_RESTORE_STATUS_DOWNLOAD: {
          return runInAction(() => {
            restore.status = UpdateRestoreStatus.DOWNLOAD;
            return true;
          });
        }
        case UpdateRestoreStatusEnum.UPDATE_RESTORE_STATUS_INSTALL: {
          return runInAction(() => {
            restore.status = UpdateRestoreStatus.INSTALL;
            return true;
          });
        }
        case UpdateRestoreStatusEnum.UPDATE_RESTORE_STATUS_REBOOT: {
          return runInAction(() => {
            restore.status = UpdateRestoreStatus.REBOOT;
            return true;
          });
        }
        case UpdateRestoreStatusEnum.UPDATE_RESTORE_STATUS_COMPLETED: {
          return runInAction(() => {
            restore.terminal_status = restore.status;
            restore.status = UpdateRestoreStatus.COMPLETED;
            pangolin.updateActiveReleaseID = release.id;
            pangolin.updateActiveRestoreID = undefined;
            return true;
          });
        }
        case UpdateRestoreStatusEnum.UPDATE_RESTORE_STATUS_CANCELLED: {
          // skip if restore service already set the status to cancelled
          if (restore.status === UpdateRestoreStatus.CANCELLED) return true;
          return runInAction(() => {
            restore.terminal_status = restore.status;
            restore.status = UpdateRestoreStatus.CANCELLED;
            pangolin.updateActiveRestoreID = undefined;
            return true;
          });
        }
        case UpdateRestoreStatusEnum.UPDATE_RESTORE_STATUS_SKIPPED: {
          return runInAction(() => {
            restore.terminal_status = restore.status;
            restore.status = UpdateRestoreStatus.SKIPPED;
            pangolin.updateActiveRestoreID = undefined;
            return true;
          });
        }
        case UpdateRestoreStatusEnum.UPDATE_RESTORE_STATUS_FAILED: {
          return runInAction(() => {
            restore.terminal_status = restore.status;
            restore.status = UpdateRestoreStatus.FAILED;
            pangolin.updateActiveRestoreID = undefined;
            return true;
          });
        }
        default: {
          return UnreachableFunction(restoreStatus);
        }
      }
    }
  }
  return false;
};

export type UnclaimPangolinResult =
  | { kind: ResultKind.OK }
  | { kind: ResultKind.SYSTEM_ERROR; status?: number; }
  | { kind: ResultKind.USER_ERROR; status?: number;
      idError?: string;
      passwordError?: string;
      updateUpgradeError?: string;
      updateRestoreError?: string;
      planFirewallSubscriptionError?: string;
    };

export type UnclaimPangolinProps = {
  domain: IAccountsDomain;
  pangolin: Pangolin;
  password: string;
};

export const unclaim = async(args: UnclaimPangolinProps): Promise<UnclaimPangolinResult> => {
  const {
    domain,
    pangolin,
    password,
  } = args;

  try {
    const request = new UnclaimPangolinRequest();
    request.setId(pangolin.id);
    request.setPassword(password);
    await http.delete('/pangolin/', { data: createRequestString(request) });
    return runInAction(() => {
      // actually remove the pangolin from the local list
      domain.pangolins.delete(pangolin.id);
      return { kind: ResultKind.OK };
    });
  } catch (error) {
    if (isHttpError(error)) {
      if (error.response?.status && 400 <= error.response.status && error.response.status < 500) {
        const httpResp = error.response;
        const response = UnclaimPangolinResponse.deserializeBinary(getResponseBytes(httpResp));
        return {
          kind: ResultKind.USER_ERROR,
          status: error.response.status,
          idError: response.getIdError(),
          passwordError: response.getPasswordError(),
          updateUpgradeError: response.getUpdateUpgradeError(),
          updateRestoreError: response.getUpdateRestoreError(),
          planFirewallSubscriptionError: response.getPlanFirewallSubscriptionError(),
        };
      }
      if (error.response?.status && 500 <= error.response.status && error.response.status < 600) {
        return { kind: ResultKind.SYSTEM_ERROR, status: error.response.status };
      }
    }
    return { kind: ResultKind.SYSTEM_ERROR };
  }
};

export type RebootPangolinArgs = { id: string; };

export type RebootPangolinResult =
  | { kind: ResultKind.OK }
  | { kind: ResultKind.USER_ERROR; status?: number; }
  | { kind: ResultKind.SYSTEM_ERROR; status?: number; };

export const reboot = async({ id }: RebootPangolinArgs): Promise<RebootPangolinResult> => {
  try {
    await http.post(`/pangolin/${id}/reboots`);
    return { kind: ResultKind.OK };
  } catch (error) {
    if (isHttpError(error)) {
      if (error.response?.status && 400 <= error.response.status && error.response.status < 500) {
        return { kind: ResultKind.USER_ERROR, status: error.response.status };
      }
      if (error.response?.status && 500 <= error.response.status && error.response.status < 600) {
        return { kind: ResultKind.SYSTEM_ERROR, status: error.response.status };
      }
    }
    return { kind: ResultKind.SYSTEM_ERROR };
  }
};
